import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Button, Form, Icon } from 'semantic-ui-react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { UserContext } from 'src/context';
import 'moment/locale/es';
import 'moment/locale/ja';
import { useTranslation } from 'react-i18next';
import { getMomentLocale } from 'src/utils/appLocale';
import { useCreateBitacora, useUpdateBitacora, useCreateBitacoraComment, analyzeBitacoraFlow } from 'src/graphql/bitacora';
import { GetInitialBalances, GetManualDocuments } from 'src/graphql/initialBalanceSidebar';
import { useCompanyMentionUsers } from 'src/hooks/useCompanyMentionUsers';
import { formatMoneyWithCurrency } from 'src/utils/formatter';
import { banksFormat } from 'src/utils/bankList';
import { importAlertState } from 'src/redux/actions/alert';
import CustomModal from 'src/components/configuracion/components/customModal';
import BitacoraEditor from 'src/modules/bitacora/BitacoraEditor';
import BitacoraUploadZone from './BitacoraUploadZone';
import BitacoraChatPanel from './BitacoraChatPanel';
import { markdownToEditorHtml } from './renderMarkdown';
import aiIcon from 'src/assets/icons/ai-icon.svg';
import SVG_BANKS from 'src/assets/svg/svg_banks';
import './BitacoraModal.scss';

const buildDefaultTitle = (date, t) => {
  const d = date ? moment(date) : moment();
  return t('bitacora.defaultTitle', {
    date: d.locale(getMomentLocale()).format(t('bitacora.dateFormat')),
  });
};

/** Asegura flowDate en formato ISOString para GraphQL */
const toFlowDateISO = (dateValue) => {
  if (!dateValue) return null;
  const m = moment(dateValue, ['YYYY-MM-DD', moment.ISO_8601], true);
  return m.isValid() ? m.utc().startOf('day').toISOString() : moment().utc().startOf('day').toISOString();
};

/** Mapea saldos del sidebar al formato de la colección logbooks (bankBalances) */
const mapToLogbookBankBalances = (allBankBalances, banksFormat) => {
  if (!Array.isArray(allBankBalances) || allBankBalances.length === 0) return [];
  return allBankBalances.map((b) => ({
    bankName: banksFormat[b.bankId] || b.bankName || b.name || b.bankId || 'Banco',
    initialBalance: Number(b.availableBalance ?? b.initialBalance ?? b.balance ?? 0) || 0,
    ...(b.label != null && b.label !== '' ? { label: String(b.label) } : {}),
  }));
};

const BitacoraModal = ({ open, onClose, bitacora, companyId, onSuccess, onEnviarClick, onRefresh }) => {
  const { t } = useTranslation('finance');
  const dispatch = useDispatch();
  const userInfo = useContext(UserContext);
  const { selectedCompany } = useSelector((state) => state.companyList);
  const currentUser = useSelector((state) => state.auth);
  const effectiveCompanyId = companyId || selectedCompany?.id;
  const currentUserEmail = userInfo?.email || userInfo?.name || null;

  const isEdit = !!bitacora?.id;
  const isDraft = bitacora?.status === 'draft';
  const isEditable = isEdit ? isDraft : true;

  const getDefaultTitle = useCallback((date) => buildDefaultTitle(date, t), [t]);

  const [flowDate, setFlowDate] = useState(moment().format('YYYY-MM-DD'));
  const [title, setTitle] = useState(getDefaultTitle());
  const [imageWithProjections, setImageWithProjections] = useState(null);
  const [imageWithoutProjections, setImageWithoutProjections] = useState(null);
  const [initialComments, setInitialComments] = useState('');
  const [emailDraft, setEmailDraft] = useState('');
  const [draftAiLoading, setDraftAiLoading] = useState(false);
  const [draftAiError, setDraftAiError] = useState('');
  const [chatOpen, setChatOpen] = useState(false);
  const commentsSectionRef = React.useRef(null);

  const createBitacora = useCreateBitacora();
  const updateBitacora = useUpdateBitacora();
  const createComment = useCreateBitacoraComment();

  const initialBalances = GetInitialBalances(effectiveCompanyId);
  const manualDocuments = GetManualDocuments(effectiveCompanyId);
  const bankBalances = initialBalances?.data?.allBankBalances || [];
  const manualBalances = manualDocuments?.data?.allInitialBalanceDocumentsByCompany || [];
  const currentUserId = currentUser?.uid;
  const mentionUsers = useCompanyMentionUsers(effectiveCompanyId, currentUserId);

  const activeBankBalances = bankBalances.filter((b) => b?.isActive !== false);
  const allBalancesForDisplay = [
    ...activeBankBalances.map((b) => ({
      type: 'bank',
      bankId: b.bankId,
      name: banksFormat[b.bankId] || b.bankName || b.name || b.bankId || t('bitacora.bank'),
      amount: Number(b.availableBalance ?? b.initialBalance ?? b.balance ?? 0) || 0,
      currencyType: b.currencyType,
    })),
    ...manualBalances.map((m) => ({
      type: 'manual',
      name: m.name || t('bitacora.manualBalance'),
      amount: Number(m.amount ?? 0) || 0,
      currencyType: m.currencyType || 'CLP',
    })),
  ];

  useEffect(() => {
    if (open) {
      setFlowDate(bitacora?.flowDate ? moment(bitacora.flowDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'));
      setTitle(bitacora?.title || getDefaultTitle(bitacora?.flowDate || flowDate));
      setInitialComments(bitacora?.initialComments || '');
      setEmailDraft(bitacora?.emailDraft || '');
      setImageWithProjections(bitacora?.imageWithProjectionsUrl ? { url: bitacora.imageWithProjectionsUrl } : null);
      setImageWithoutProjections(bitacora?.imageWithoutProjectionsUrl ? { url: bitacora.imageWithoutProjectionsUrl } : null);
      setDraftAiError('');
    }
  }, [open, bitacora]);

  const canSubmit = imageWithProjections && imageWithoutProjections;

  const hasBothImagesForAi =
    (imageWithProjections?.url || bitacora?.imageWithProjectionsUrl) &&
    (imageWithoutProjections?.url || bitacora?.imageWithoutProjectionsUrl);
  const canGenerateDraftWithAi = Boolean(hasBothImagesForAi);
  const canOpenChat =
    isEditable &&
    bitacora?.id &&
    hasBothImagesForAi;

  /** Si ya existe contenido en comentarios iniciales (borrador creado) */
  const hasDraftContent = Boolean(
    (initialComments || '').replace(/<[^>]*>/g, '').trim().length > 20,
  );
  const hasInitialCommentsContent = Boolean(
    (initialComments || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .trim().length > 0,
  );

  const handleImageChange = (type, file) => {
    if (!file || !isEditable) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target.result;
      if (type === 'with') setImageWithProjections({ file, url });
      else setImageWithoutProjections({ file, url });
    };
    reader.readAsDataURL(file);
  };

  const handleUploadWith = (file) => handleImageChange('with', file);
  const handleUploadWithout = (file) => handleImageChange('without', file);

  const handleGenerateDraftWithAi = useCallback(async () => {
    if (draftAiLoading || !canGenerateDraftWithAi) return;
    const urlWith = imageWithProjections?.url || bitacora?.imageWithProjectionsUrl;
    const urlWithout = imageWithoutProjections?.url || bitacora?.imageWithoutProjectionsUrl;
    if (!urlWith || !urlWithout) return;
    setDraftAiLoading(true);
    setDraftAiError('');
    try {
      const balancesMap = {};
      (activeBankBalances || []).forEach((b) => {
        const name = banksFormat[b.bankId] || b.bankName || b.name || b.bankId || 'Banco';
        const balance = Number(b.availableBalance ?? b.initialBalance ?? b.balance ?? 0);
        balancesMap[name] = Number.isFinite(balance) ? balance : 0;
      });
      (manualBalances || []).forEach((m) => {
        const name = m.name || 'Saldo manual';
        const amount = Number(m.amount ?? 0);
        balancesMap[name] = Number.isFinite(amount) ? amount : 0;
      });
      if (Object.keys(balancesMap).length === 0) {
        balancesMap['Saldo inicial'] = 0;
      }
      const result = await analyzeBitacoraFlow({
        imageWithProjectionsUrl: urlWith,
        imageWithoutProjectionsUrl: urlWithout,
        companyName: selectedCompany?.name || selectedCompany?.alias || '',
        flowDate: flowDate ? moment(flowDate).locale(getMomentLocale()).format(t('bitacora.dateFormat')) : '',
        bankBalances: balancesMap,
      });
      const text = result?.analysis || '';
      if (text) setInitialComments(markdownToEditorHtml(text));
      if (result?.emailDraft) setEmailDraft(markdownToEditorHtml(result.emailDraft) || '<p></p>');
    } catch (err) {
      setDraftAiError(err?.message || t('bitacora.draftAiError'));
    } finally {
      setDraftAiLoading(false);
    }
  }, [draftAiLoading, canGenerateDraftWithAi, imageWithProjections, imageWithoutProjections, bitacora, activeBankBalances, manualBalances, selectedCompany, flowDate, dispatch]);

  const handleDebouncedCommentsUpdate = useCallback(async (contenidoHtml) => {
    if (!isEdit || !isEditable || !bitacora?.id) return;
    try {
      const input = { initialComments: contenidoHtml };
      if (currentUserEmail) input.updatedBy = currentUserEmail;
      await updateBitacora.executeMutation(bitacora.id, input);
    } catch (e) {
      // Keep UX smooth: autosave failure won't block manual save/send actions.
    }
  }, [isEdit, isEditable, bitacora, updateBitacora, currentUserEmail]);

  const handleSaveDraft = async () => {
    if (!canSubmit) {
      dispatch(importAlertState({ content: t('bitacora.completeImages'), state: 'error', duration: 4 }));
      return;
    }
    try {
      if (isEdit) {
        const bankBalancesPayload = mapToLogbookBankBalances(bankBalances, banksFormat);
        const updateInput = {
          initialComments: initialComments ?? '',
          emailDraft: emailDraft ?? '',
          imageWithProjectionsUrl: imageWithProjections?.url ?? null,
          imageWithoutProjectionsUrl: imageWithoutProjections?.url ?? null,
          bankBalances: bankBalancesPayload,
        };
        if (currentUserEmail) updateInput.updatedBy = currentUserEmail;
        const { error } = await updateBitacora.executeMutation(String(bitacora.id), updateInput);
        if (error) throw error;
      } else {
        const flowDateISO = toFlowDateISO(flowDate);
        const { error } = await createBitacora.executeMutation({
          flowDate: flowDateISO,
          title: (title && title.trim()) || getDefaultTitle(flowDate),
          initialComments: initialComments ?? '',
          emailDraft: emailDraft ?? '',
          imageWithProjectionsUrl: imageWithProjections?.url ?? null,
          imageWithoutProjectionsUrl: imageWithoutProjections?.url ?? null,
          companyId: effectiveCompanyId != null ? String(effectiveCompanyId) : '',
          status: 'draft',
        });
        if (error) throw error;
      }
      dispatch(importAlertState({ content: 'Borrador guardado', state: 'success', duration: 3 }));
      onSuccess();
      onClose();
    } catch (e) {
      const message = e?.message || e?.graphQLErrors?.[0]?.message || t('bitacora.saveError');
      dispatch(importAlertState({ content: message, state: 'error', duration: 5 }));
    }
  };

  const handleEnviar = async () => {
    if (!canSubmit) {
      dispatch(importAlertState({ content: t('bitacora.completeImages'), state: 'error', duration: 4 }));
      return;
    }
    let savedBitacora = bitacora;
    try {
      if (!isEdit || !bitacora?.id) {
        const flowDateISO = toFlowDateISO(flowDate);
        const { data, error } = await createBitacora.executeMutation({
          flowDate: flowDateISO,
          title: (title && title.trim()) || getDefaultTitle(flowDate),
          initialComments: initialComments ?? '',
          emailDraft: emailDraft ?? '',
          imageWithProjectionsUrl: imageWithProjections?.url ?? null,
          imageWithoutProjectionsUrl: imageWithoutProjections?.url ?? null,
          companyId: effectiveCompanyId != null ? String(effectiveCompanyId) : '',
          status: 'draft',
        });
        if (error) throw error;
        savedBitacora = data?.createBitacora || savedBitacora;
      } else {
        const bankBalancesPayload = mapToLogbookBankBalances(bankBalances, banksFormat);
        const updateInput = {
          initialComments: initialComments ?? '',
          emailDraft: emailDraft ?? '',
          imageWithProjectionsUrl: imageWithProjections?.url ?? null,
          imageWithoutProjectionsUrl: imageWithoutProjections?.url ?? null,
          bankBalances: bankBalancesPayload,
        };
        if (currentUserEmail) updateInput.updatedBy = currentUserEmail;
        const { error } = await updateBitacora.executeMutation(String(bitacora.id), updateInput);
        if (error) throw error;
      }
      onClose();
      onEnviarClick?.(savedBitacora);
    } catch (e) {
      const message = e?.message || e?.graphQLErrors?.[0]?.message || t('bitacora.saveError');
      dispatch(importAlertState({ content: message, state: 'error', duration: 5 }));
    }
  };

  const customActions = isEditable ? (
    <div className="bitacora-modal-actions">
      <Button basic onClick={onClose} className="bitacora-modal-btn">
        {t('common.cancel')}
      </Button>
      <Button
        onClick={handleSaveDraft}
        loading={createBitacora.fetching || updateBitacora.fetching}
        className="bitacora-modal-btn-teal"
        disabled={!hasInitialCommentsContent}
      >
        {t('bitacora.saveDraft')}
      </Button>
    </div>
  ) : (
    <div className="bitacora-modal-actions">
      <Button onClick={onClose} className="bitacora-modal-btn-teal">
        {t('common.close')}
      </Button>
    </div>
  );

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title={isEdit ? t('bitacora.editTitle') : t('bitacora.create')}
      classNameModal="bitacora-modal"
      classNameContent="bitacora-modal-content-wrapper"
      scrolling={false}
      content={
        <div className="bitacora-modal-content">
          <Form>
            <Form.Field className="bitacora-modal-field">
              <label className="bitacora-modal-section-label">{t('bitacora.titleField')}</label>
              <Form.Input
                value={title}
                onChange={(_, { value }) => setTitle(value)}
                placeholder={getDefaultTitle(flowDate)}
                disabled={!isEditable}
                className="bitacora-modal-title-input"
              />
            </Form.Field>

            <Form.Field className="bitacora-modal-field bitacora-modal-field--uploads-row">
              <div className="bitacora-modal-uploads-row">
                <div className="bitacora-modal-upload-cell">
                  <label className="bitacora-modal-section-label">{t('bitacora.cashflowWithProjections')} {!isEditable && t('bitacora.readOnly')}</label>
                  <BitacoraUploadZone
                    label={t('bitacora.cashflowWithProjections')}
                    value={imageWithProjections}
                    onChange={handleUploadWith}
                    onClear={() => setImageWithProjections(null)}
                    disabled={!isEditable}
                    accept="image/*"
                  />
                </div>
                <div className="bitacora-modal-upload-cell">
                  <label className="bitacora-modal-section-label">{t('bitacora.cashflowWithoutProjections')} {!isEditable && t('bitacora.readOnly')}</label>
                  <BitacoraUploadZone
                    label={t('bitacora.cashflowWithoutProjections')}
                    value={imageWithoutProjections}
                    onChange={handleUploadWithout}
                    onClear={() => setImageWithoutProjections(null)}
                    disabled={!isEditable}
                    accept="image/*"
                  />
                </div>
              </div>
            </Form.Field>

            <Form.Field className="bitacora-modal-field">
              <label className="bitacora-modal-section-label">{t('bitacora.initialBalances')}</label>
              <div className="bitacora-modal-banks">
                {allBalancesForDisplay.length > 0 ? (
                  allBalancesForDisplay.map((item, i) => (
                    <div key={i} className="bitacora-modal-bank-row">
                      <span className="bitacora-modal-bank-row__label">
                        {item.type === 'bank' && item.bankId ? (
                          <span className="bitacora-modal-bank-row__icon">
                            <img src={SVG_BANKS(item.bankId)} alt="" aria-hidden />
                          </span>
                        ) : (
                          <span className="bitacora-modal-bank-row__icon bitacora-modal-bank-row__icon--manual" title={t('bitacora.manualBalance')}>
                            <Icon name="money bill alternate outline" style={{ margin: 0, fontSize: '14px' }} />
                          </span>
                        )}
                        <span>{item.name}</span>
                      </span>
                      <span>{formatMoneyWithCurrency(item.amount, item.currencyType)}</span>
                    </div>
                  ))
                ) : (
                  <span className="bitacora-modal-empty">{t('bitacora.noBalances')}</span>
                )}
              </div>
            </Form.Field>

            <div ref={commentsSectionRef}>
            <Form.Field className="bitacora-modal-field">
              <label className="bitacora-modal-section-label">{t('bitacora.internalAnalysis')}</label>
              {draftAiError && (
                <div className="bitacora-modal-ai-draft-error">{draftAiError}</div>
              )}
              {chatOpen && canOpenChat && (
                <div className="bitacora-modal-chat-wrap">
                  <BitacoraChatPanel
                    bitacora={bitacora}
                    onClose={() => setChatOpen(false)}
                    onApplyReply={(reply) => {
                      setInitialComments(markdownToEditorHtml(reply));
                      dispatch(importAlertState({ content: t('bitacora.analysisUpdated'), state: 'success', duration: 3 }));
                      setTimeout(() => {
                        commentsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 100);
                    }}
                    onAddAsComment={async (reply) => {
                      try {
                        const { error } = await createComment.executeMutation(bitacora.id, { comment: reply, source: 'ai' });
                        if (error) throw error;
                        dispatch(importAlertState({ content: t('bitacora.aiCommentAdded'), state: 'success', duration: 3 }));
                        onRefresh?.();
                      } catch (e) {
                        dispatch(importAlertState({ content: e?.message || t('bitacora.aiCommentError'), state: 'error', duration: 5 }));
                      }
                    }}
                  />
                </div>
              )}
              <BitacoraEditor
                contenidoHtml={initialComments}
                onChange={setInitialComments}
                onDebouncedChange={handleDebouncedCommentsUpdate}
                placeholder={t('bitacora.analysisPlaceholder')}
                disabled={!isEditable}
                mentionUsers={mentionUsers}
                toolbarExtra={isEditable && ((canOpenChat && hasDraftContent) || (canGenerateDraftWithAi && !hasDraftContent)) && (
                  <div className="bitacora-modal-toolbar-extra">
                    {!chatOpen && canOpenChat && hasDraftContent && (
                      <button
                        type="button"
                        className="bitacora-modal-ai-btn"
                        onClick={() => setChatOpen((v) => !v)}
                        title={t('bitacora.openAiChat')}
                      >
                        <span className="bitacora-modal-ai-btn__content">
                          <img src={aiIcon} alt="IA" className="bitacora-modal-ai-btn__icon" />
                          <span>{t('bitacora.aiChat')}</span>
                        </span>
                      </button>
                    )}
                    {!hasDraftContent && canGenerateDraftWithAi && (
                      <button
                        type="button"
                        className={`bitacora-modal-ai-btn${draftAiLoading ? ' is-loading' : ''}`}
                        onClick={handleGenerateDraftWithAi}
                        disabled={draftAiLoading}
                        title={draftAiLoading ? t('bitacora.generating') : t('bitacora.generateInitialAi')}
                      >
                        <span className="bitacora-modal-ai-btn__content">
                          <img
                            src={aiIcon}
                            alt="IA"
                            className={`bitacora-modal-ai-btn__icon${draftAiLoading ? ' is-loading' : ''}`}
                          />
                          <span>{draftAiLoading ? t('bitacora.generating') : t('bitacora.generateDraftAi')}</span>
                        </span>
                      </button>
                    )}
                  </div>
                )}
              />
            </Form.Field>
            </div>
          </Form>
        </div>
      }
      customActions={customActions}
      size="large"
      dimmer={{ style: { zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.5)' } }}
      modalStyle={{
        maxWidth: '90%',
        minWidth: '500px',
        maxHeight: '85vh',
        overflow: 'hidden',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    />
  );
};

export default BitacoraModal;
